// src/components/Form8919Questions.js
import React from 'react';
import { HelpCircle, FileText, PlusCircle, Trash2 } from 'react-feather';

/**
 * A component to collect all necessary information for IRS Form 8919.
 * It guides the user through a checklist of requirements before showing the full form.
 * @param {object} props - The component props.
 * @param {object} props.formData - The main form data object from the App state.
 * @param {function} props.onDataChange - The main handler from App.js to update the state.
 */
function Form8919Questions({ formData, onDataChange }) {
  // Ensure form8919 and its firms array exist to prevent errors on initial render
  const { form8919 = { firms: [] } } = formData;

  // --- Conditional Rendering Logic ---
  // Determines if the "Reason Code" section should be visible
  const allRequirementsMet =
    form8919.performedServicesForFirm &&
    form8919.shouldHaveBeenEmployee &&
    form8919.noTaxesWithheld &&
    form8919.reasonCodeApplies;

  // Determines if the main form details (SS totals, firms list) should be visible
  const showFormDetails = allRequirementsMet && form8919.reasonCode && form8919.reasonCode !== '';

  // --- Event Handlers for Managing the Dynamic Firms List ---

  const handleAddFirm = () => {
    const newFirm = {
      id: `firm-${Date.now()}`,
      name: '',
      address: '',
      tin: '',
      wages: '',
      startDate: '',
      endDate: '',
    };
    const updatedFirms = [...(form8919.firms || []), newFirm];
    // Use a synthetic event to update the nested state in App.js
    onDataChange({ target: { name: 'form8919.firms', value: updatedFirms } });
  };

  const handleDeleteFirm = (idToDelete) => {
    const updatedFirms = form8919.firms.filter(firm => firm.id !== idToDelete);
    onDataChange({ target: { name: 'form8919.firms', value: updatedFirms } });
  };

  const handleFirmChange = (id, field, value) => {
    const updatedFirms = form8919.firms.map(firm =>
      firm.id === id ? { ...firm, [field]: value } : firm
    );
    onDataChange({ target: { name: 'form8919.firms', value: updatedFirms } });
  };

  return (
    <div className="form-section" style={{ marginTop: '2rem' }}>
      <div className="content-box-header">
        <div className="header-title-with-help">
          <h2><FileText size={20} className="icon-orange" /> Form 8919: Uncollected Social Security and Medicare Tax</h2>
          <div className="has-tooltip header-help-icon" data-tooltip="Use this form to figure and report your share of uncollected Social Security and Medicare taxes due on your compensation.">
            <HelpCircle size={18} />
          </div>
        </div>
      </div>

      {/* Section 1: Requirements Checklist */}
      <div className="dependent-card">
        <h4 style={{ marginBottom: '1rem' }}>Filing Requirements Checklist</h4>
        <p className="form-description">You must file Form 8919 if <strong>all</strong> of the following apply.</p>
        <div className="form-group checkbox-group"><input type="checkbox" id="performedServicesForFirm" name="form8919.performedServicesForFirm" checked={form8919.performedServicesForFirm || false} onChange={onDataChange} /><label htmlFor="performedServicesForFirm">You performed services for a firm.</label></div>
        <div className="form-group checkbox-group"><input type="checkbox" id="shouldHaveBeenEmployee" name="form8919.shouldHaveBeenEmployee" checked={form8919.shouldHaveBeenEmployee || false} onChange={onDataChange} /><label htmlFor="shouldHaveBeenEmployee">You believe you should have been treated as an employee.</label></div>
        <div className="form-group checkbox-group"><input type="checkbox" id="noTaxesWithheld" name="form8919.noTaxesWithheld" checked={form8919.noTaxesWithheld || false} onChange={onDataChange} /><label htmlFor="noTaxesWithheld">The firm did not withhold Social Security and Medicare taxes.</label></div>
        <div className="form-group checkbox-group"><input type="checkbox" id="reasonCodeApplies" name="form8919.reasonCodeApplies" checked={form8919.reasonCodeApplies || false} onChange={onDataChange} /><label htmlFor="reasonCodeApplies">One of the Reason Codes below applies to your situation.</label></div>
      </div>

      {/* Section 2: Reason Codes (Conditional) */}
      {allRequirementsMet && (
        <div className="dependent-card" style={{ marginTop: '1.5rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Reason Code</h4>
          <p className="form-description">Choose the one reason that best applies.</p>
          <div className="radio-group vertical">
            <div className="radio-option"><input type="radio" id="reasonA" name="form8919.reasonCode" value="A" checked={form8919.reasonCode === 'A'} onChange={onDataChange} /><label htmlFor="reasonA"><strong>Code A:</strong> You filed Form SS-8 and the IRS issued a determination letter stating you are an employee.</label></div>
            <div className="radio-option"><input type="radio" id="reasonC" name="form8919.reasonCode" value="C" checked={form8919.reasonCode === 'C'} onChange={onDataChange} /><label htmlFor="reasonC"><strong>Code C:</strong> You received other correspondence from the IRS stating you are an employee.</label></div>
            <div className="radio-option"><input type="radio" id="reasonG" name="form8919.reasonCode" value="G" checked={form8919.reasonCode === 'G'} onChange={onDataChange} /><label htmlFor="reasonG"><strong>Code G:</strong> You filed Form SS-8 with the IRS and have not yet received a reply.</label>{form8919.reasonCode === 'G' && <small className="info-note"><strong>Note:</strong> You must file Form SS-8 on or before the date you file your tax return when using code G.</small>}</div>
            <div className="radio-option"><input type="radio" id="reasonH" name="form8919.reasonCode" value="H" checked={form8919.reasonCode === 'H'} onChange={onDataChange} /><label htmlFor="reasonH"><strong>Code H:</strong> You received both a W-2 and a 1099 from the same firm, and the 1099 income should have been on the W-2.</label>{form8919.reasonCode === 'H' && <small className="info-note">If reason code H applies, you should not file Form SS-8.</small>}</div>
          </div>
        </div>
      )}

      {/* Section 3: All Other Form Details (Conditional) */}
      {showFormDetails && (
        <div className="form-section" style={{ marginTop: '1.5rem' }}>
          
          <div className="dependent-card">
            <h4 style={{ marginBottom: '1rem' }}>Social Security Limit Information</h4>
            <p className="form-description">Enter the totals from all other sources (like your W-2s) to correctly calculate the Social Security tax limit.</p>
            <div className="form-group">
              <label>Total Social Security wages from other sources</label>
              <input type="number" name="form8919.totalSSWages" value={form8919.totalSSWages ?? ''} onChange={onDataChange} />
              <small>This is the total from Box 3 of all your W-2s.</small>
            </div>
            <div className="form-group" style={{marginTop: '1rem'}}>
              <label>Total Social Security tips from other sources</label>
              <input type="number" name="form8919.totalSSTips" value={form8919.totalSSTips ?? ''} onChange={onDataChange} />
              <small>This is the total from Box 7 of all your W-2s.</small>
            </div>
            <div className="form-group" style={{marginTop: '1rem'}}>
              <label>Unreported tips subject to Social Security tax</label>
              <input type="number" name="form8919.unreportedTipsSubjectToSS" value={form8919.unreportedTipsSubjectToSS ?? ''} onChange={onDataChange} />
              <small>This is the amount from Form 4137, line 10.</small>
            </div>
          </div>
          
          <div className="form-section-divider"></div>

          <div className="content-box-header" style={{ marginBottom: '0.5rem' }}>
            <h4>Firm Information</h4>
            <div className="header-actions"><button onClick={handleAddFirm} className="add-item-button"><PlusCircle size={16} /><span>Add Firm</span></button></div>
          </div>
          <p className="form-description">Enter the details for each firm that paid you wages without withholding Social Security and Medicare taxes.</p>
          {(!form8919.firms || form8919.firms.length === 0) && <p className="empty-state-message">No firms added yet.</p>}
          {form8919.firms?.map((firm, index) => (
            <div key={firm.id} className="dependent-card">
              <div className="dependent-card-header"><h4>Firm #{index + 1}</h4><button onClick={() => handleDeleteFirm(firm.id)} className="delete-card-button" title="Remove Firm"><Trash2 size={18} /></button></div>
              <div className="form-row"><div className="form-group"><label>Name of Firm</label><input type="text" value={firm.name} onChange={e => handleFirmChange(firm.id, 'name', e.target.value)} /></div><div className="form-group"><label>Firm's EIN or SSN</label><input type="text" value={firm.tin} onChange={e => handleFirmChange(firm.id, 'tin', e.target.value)} /></div></div>
              <div className="form-group"><label>Address of Firm</label><input type="text" value={firm.address} onChange={e => handleFirmChange(firm.id, 'address', e.target.value)} /></div>
              <div className="form-group" style={{marginTop: '1rem'}}><label>Total Wages Received from Firm</label><input type="number" value={firm.wages ?? ''} onChange={e => handleFirmChange(firm.id, 'wages', e.target.value)} /></div>
              <div className="form-row" style={{marginTop: '1rem'}}><div className="form-group"><label>Start Date of Service</label><input type="date" value={firm.startDate} onChange={e => handleFirmChange(firm.id, 'startDate', e.target.value)} /></div><div className="form-group"><label>End Date of Service</label><input type="date" value={firm.endDate} onChange={e => handleFirmChange(firm.id, 'endDate', e.target.value)} /></div></div>
            </div>
          ))}

          {form8919.firms?.length > 0 && (
            <div className="dependent-card" style={{ marginTop: '1.5rem', backgroundColor: 'var(--bg-main)' }}>
              <div className="form-group"><label>Tax Already Paid on Tips or Group-Term Life Insurance (Line 12)</label><input type="number" name="form8919.taxPaidOnTips" value={form8919.taxPaidOnTips ?? ''} onChange={onDataChange} /><small>Enter any Social Security and Medicare tax you already paid on these wages.</small></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Form8919Questions;
